import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RiDeleteBin7Line, RiEditLine } from "react-icons/ri";
import { ArrowLeftIcon, DeleteIcon, EditIcon, User, Users, X } from "lucide-react";
import {
  Box,
  Flex,
  Text,
  Stack,
  Input,
  Select,
  SimpleGrid,
  Button,
  Checkbox,
  Spinner,
  Alert,
  AlertIcon,
  IconButton,
  useBreakpointValue,
  useToast,
  Grid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Image,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  FormControl,
  FormLabel,
  Badge,
  GridItem,
  Card,
} from "@chakra-ui/react";

import { fetchCreditDetails, updateCreditStatus } from "../../redux/creditSlice";
import { get_all_vendor_list } from "../../redux/vendorSlice";
import { get_full_product_list } from "../../redux/productSlice";
import CustomSingleInput from "../../components/CustomSingleInput";
import CreditApprovalHistory from "./CreditApprovalHistory";
import { ChevronDownIcon } from "@chakra-ui/icons";
import VendorSelection from "./VendorSelection";
import CreditApprovalSection from "./CreditApprovalSection";
import CustomButton from "../../components/CustomButton";
import { BiArrowBack } from "react-icons/bi";
import GasCylinderLoader from "../../components/GasCylinderLoader";

const CreditDetails = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const toast = useToast();
  const id = location.state?.id;
  const isViewOnly = location.state?.isViewOnly || false;
  const [openingBalance, setOpeningBalance] = useState("");
  const [closingBalance, setClosingBalance] = useState("");
  const [isFirstApproval, setIsFirstApproval] = useState(true);
  const [discounts, setDiscounts] = useState([]);
  const [creditLimit, setCreditLimit] = useState("");
  const [creditLimitControl, setCreditLimitControl] = useState("2");
  const [creditDays, setCreditDays] = useState("");
  const [creditDaysControl, setCreditDaysControl] = useState("2");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [comment, setComment] = useState("");
  const [editingDiscountIndex, setEditingDiscountIndex] = useState(null);
  const [isEditingCredit, setIsEditingCredit] = useState(false);
  const [creditHistory, setCreditHistory] = useState([]);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState([]);
  const [vendorError, setVendorError] = React.useState("");

  const initialCreditData = useRef({
    creditLimit: "",
    creditLimitControl: "2",
    creditDays: "",
    creditDaysControl: "2",
    discounts: []
  });

  const previousCreditFields = useRef({
    creditLimit: null,
    creditLimitControl: null,
    creditDays: null,
    creditDaysControl: null
  });

  // const [isMultiSelectMode, setIsMultiSelectMode] = useState(false); // ADD THIS LINE

  const primaryLight = "#e6f4ec";
  const navigate = useNavigate();
  const previousTab = location.state?.previousTab || 0;
  const previousTabName = location.state?.previousTabName || "applications";

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isCommentOpen, onOpen: onCommentOpen, onClose: onCommentClose } = useDisclosure();
  const [selectedDoc, setSelectedDoc] = useState(null);

  const { details, detailsLoading, detailsError } = useSelector((state) => state.credit);
  const { all_vendor_list, all_loading } = useSelector((state) => state.vendors);
  const { full_product_list } = useSelector((state) => state.products);

  const gridColumns = useBreakpointValue({ base: 1, sm: 2, md: 3, lg: 5 });
  const handleGoBack = useCallback(() => {
    navigate("/credit", {
      state: {
        activeTab: previousTab,
        tabName: previousTabName
      },
      replace: true
    });
  }, [navigate, previousTab, previousTabName]);
  // FIXED: Check application status - Get status from the details.details array (history)
  const currentStatus = useMemo(() => {
    // Get status from the latest entry in details array
    if (details?.details?.[0]?.data && details.details[0].data.length > 0) {
      const latestEntry = details.details[0].data[details.details[0].data.length - 1];
      return latestEntry.status;
    }
    return null;
  }, [details]);

  const isUnderReview = currentStatus === "0";
  const isApproved = currentStatus === "3";
  const isRejected = currentStatus === "1";

  const selectedProductIds = useMemo(
    () => discounts.map((d) => d.product).filter(Boolean),
    [discounts]
  );

  // Determine if edit button and credit history should be shown
  const shouldShowEditButton = useMemo(() => {
    // Show edit button for any status except under review (0)
    return currentStatus && currentStatus !== "0";
  }, [currentStatus]);

  const shouldShowCreditHistory = useMemo(() => {
    return !isUnderReview; // Show credit history when NOT under review (status != 0)
  }, [isUnderReview]);

  // Memoized values
  const customerName = useMemo(() => {
    if (!details) return "";
    return details.customer_type === "0"
      ? details.full_name
      : details.legalname || details.tradename;
  }, [details]);

  const haveCreditFieldsChanged = useCallback(() => {
    // If no previous values exist, treat as changed (for initial setup)
    if (previousCreditFields.current.creditLimit === null) {
      return true;
    }
    
    const currentFields = {
      creditLimit,
      creditLimitControl, 
      creditDays,
      creditDaysControl
    };
    
    const previousFields = previousCreditFields.current;
    
    const hasChanged = (
      previousFields.creditLimit !== currentFields.creditLimit ||
      previousFields.creditLimitControl !== currentFields.creditLimitControl ||
      previousFields.creditDays !== currentFields.creditDays ||
      previousFields.creditDaysControl !== currentFields.creditDaysControl
    );
    
    console.log("Field change check:", {
      previous: previousFields,
      current: currentFields,
      hasChanged
    });
    
    return hasChanged;
  }, [creditLimit, creditLimitControl, creditDays, creditDaysControl]);

  
  const documentTypes = useMemo(() => [
    { label: "GST Certificate", key: "gst_certificate" },
    { label: "Aadhaar Card", key: "aadhar_card" },
    { label: "PAN Card", key: "pan_card" },
    { label: "Business License", key: "business_license" },
    { label: "Photo Upload", key: "image" },
    { label: "Bank Statement", key: "bank_statement" },
  ], []);

  // Get the latest credit history entry
  const latestCreditEntry = useMemo(() => {
    if (!creditHistory || creditHistory.length === 0) return null;
    return creditHistory[creditHistory.length - 1];
  }, [creditHistory]);

  // Callbacks
  const handleAddDiscount = useCallback(() => {
    const newDiscount = {
      type: "",
      product: "",
      rspCyl: 0,
      rspKg: 0,
      discount: 0,
      taxableAmount: 0, // Add this line
      effectiveDate: new Date().toISOString().split("T")[0],
      isNew: true,
      tempId: `new-${Date.now()}`, // Unique temporary ID
    };

    // Add new discount at the beginning of the array (top)
    setDiscounts(prev => [newDiscount, ...prev]);
    setEditingDiscountIndex(0); // Edit the first item (newly added)
  }, []);

  const getProductsByType = useCallback((type, currentIndex = null) => {
    if (!type) return [];

    const typeProducts = full_product_list.filter((p) => p.type === type);

    const usedProductIds = discounts
      .filter((discount, index) => {
        // Don't exclude the product at the current index being edited
        if (index === currentIndex) return false;
        // Only exclude products of the same type AND with actual product values
        return discount.type === type && discount.product && discount.product !== "";
      })
      .map(discount => discount.product);

    return typeProducts.filter(product => !usedProductIds.includes(product.uuid));
  }, [full_product_list, discounts]);

  const handleChange = (index, field, value) => {
    const updatedDiscounts = [...discounts];

    // Update the changed field
    updatedDiscounts[index] = {
      ...updatedDiscounts[index],
      [field]: value,
    };

    // Reset product-related fields if type changes
    if (field === "type") {
      updatedDiscounts[index].product = "";
      updatedDiscounts[index].rspCyl = 0;
      updatedDiscounts[index].rspKg = 0;
      updatedDiscounts[index].discountedAmount = 0;
      updatedDiscounts[index].taxableAmount = 0;
    }

    // Update RSP values when product changes
    if (field === "product" && value) {
      const matchedProduct = full_product_list.find(p => p.uuid === value);
      if (matchedProduct) {
        updatedDiscounts[index].rspCyl = matchedProduct.rsp || 0;
        updatedDiscounts[index].rspKg = matchedProduct.capacity
          ? (parseFloat(matchedProduct.rsp) / parseFloat(matchedProduct.capacity)).toFixed(2)
          : 0;
      }
    }

    // Recalculate discountedAmount and taxableAmount
    const { rspCyl, discount } = updatedDiscounts[index];
    if (rspCyl !== undefined && discount !== undefined && discount !== "") {
      const discountedAmount = ((parseFloat(rspCyl) * parseFloat(discount)) / 100).toFixed(2);
      updatedDiscounts[index].discountedAmount = Number(discountedAmount);

      updatedDiscounts[index].taxableAmount = (
        parseFloat(rspCyl) - parseFloat(discountedAmount)
      ).toFixed(2);
    } else {
      // Reset if discount or RSP is empty
      updatedDiscounts[index].discountedAmount = 0;
      updatedDiscounts[index].taxableAmount = 0;
    }

    setDiscounts(updatedDiscounts);
  };

  const handleDelete = useCallback((index) => {
    setDiscounts((prev) => prev.filter((_, i) => i !== index));
    if (editingDiscountIndex === index) {
      setEditingDiscountIndex(null);
    } else if (editingDiscountIndex > index) {
      setEditingDiscountIndex(editingDiscountIndex - 1);
    }
  }, [editingDiscountIndex]);
  

  const handleEdit = useCallback((index) => {
    setEditingDiscountIndex(index);
  }, []);

  const handleSaveDiscount = useCallback((index) => {
    setEditingDiscountIndex(null);
    setDiscounts(prev => {
      const updated = [...prev];
      if (updated[index].isNew) {
        updated[index].isNew = false;

        // Remove completely empty new discounts
        if (!updated[index].type && !updated[index].product && !updated[index].discount) {
          return updated.filter((_, i) => i !== index);
        }
      }
      return updated;
    });
  }, []);

    const detailId = useMemo(() => {
    if (details?.details?.[0]?.data && details.details[0].data.length > 0) {
      const sortedDetails = [...details.details[0].data].sort((a, b) => b.id - a.id);
      return sortedDetails[0].id;
    }

    return null;
  }, [details]);

  const handleApiCall = useCallback(async (action, status, additionalData = {}) => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("action", action);
      formData.append("status", status);
      formData.append("detail_id",detailId);
      formData.append("id", id);
      formData.append("userid", details.customerid || "1");

      // Add additional data to formData
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });

      const response = await fetch("https://gaswale.vensframe.com/api/admin/credit.php", {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Success",
          description: result.message || "Operation completed successfully",
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        // Refresh the credit details
        dispatch(fetchCreditDetails(id));

        return result;
      } else {
        throw new Error(result.message || "Operation failed");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Something went wrong",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [id, toast, dispatch, details]);



  const handleApprove = useCallback(
    async (approvalStatus) => {
      if (!creditLimit || !creditDays) {
        toast({
          title: "Validation Error",
          description: "Credit Limit and Credit Days are required fields.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Set closing balance equal to opening balance on approval
      const finalClosingBalance = approvalStatus === "3" ? openingBalance : closingBalance;

      const additionalData = {
        cc_type_limit: "2",
        cd_type_limit: "2",
        credit_limit: creditLimit,
        credit_days: creditDays,
        opening_balance: openingBalance || details.opening_balance || "100.00",
        closing_balance: finalClosingBalance || openingBalance || details.closing_balance || "100.00",
        customerid: details.customerid,
        id: id,
        detail_id: detailId
      };

      // Update local state
      if (approvalStatus === "3") {
        setClosingBalance(openingBalance);
      }

      // If this is the first approval, mark it as no longer the first
      if (isFirstApproval && approvalStatus === "3") {
        setIsFirstApproval(false);
      }

      const newDiscounts = discounts.filter(discount => discount.isNew);
      newDiscounts.forEach((discount, index) => {
        if (discount.product) {
          additionalData[`prod_code[${index}]`] = discount.product;
          additionalData[`discount[${index}]`] = discount.discount;
          additionalData[`discount_amount[${index}]`] = discount.discountedAmount;
          additionalData[`effectivedate[${index}]`] = discount.effectiveDate;
          additionalData[`credit_id[${index}]`] = id;
        }
      });

      selectedVendor.forEach((vendorId, vIndex) => {
        additionalData[`vendor_id[${vIndex}]`] = vendorId;
      });

      try {
        const result = await handleApiCall("update_status", approvalStatus, additionalData);

        if (isFirstApproval || haveCreditFieldsChanged()) {
          const newHistoryEntry = {
            id: detailId || Date.now().toString(),
            ref_id: id,
            status: approvalStatus,
            credit_limit: creditLimit,
            credit_days: creditDays,
            cc_type_limit: "2",
            cd_type_limit: "2",
            datetime: new Date().toISOString().replace('T', ' ').substring(0, 19),
            message: isFirstApproval ? "Application approved" : "Credit settings updated"
          };

          setCreditHistory(prev => [...prev, newHistoryEntry]);
          previousCreditFields.current = {
            creditLimit,
            creditLimitControl:"2",
            creditDays,
            creditDaysControl:"2"
          };
          dispatch(fetchCreditDetails(id));
          setEditingDiscountIndex(null);
          dispatch(updateCreditStatus({
            creditId: id,
            status: approvalStatus,
            creditLimit: creditLimit,
            creditDays: creditDays
          }));

          setIsEditingCredit(false);
          setEditingDiscountIndex(null);
        }
      } catch (error) {
        console.error("Approval failed:", error);
      }
    },
    [creditLimit, creditDays, creditLimitControl, creditDaysControl, discounts, id, details, handleApiCall, toast, detailId, dispatch, openingBalance, closingBalance, isFirstApproval]
  );

  const handleOpeningBalanceChange = useCallback((value) => {
    setOpeningBalance(value);
    // If status is under review (0), sync closing balance with opening balance
    if (isUnderReview) {
      setClosingBalance(value);
    }
  }, [isUnderReview]);

  const handleReject = useCallback(async () => {
    try {
      const additionalData = {
        id: id,
        userid: details.customerid || "1",
        status: "1"
      };

      await handleApiCall("update_status", "1", additionalData);
    } catch (error) {
      console.error("Rejection failed:", error);
    }
  }, [handleApiCall, id, details]);

  // FIXED: Updated handleSaveChanges to work correctly for approved status
  const handleSaveChanges = useCallback(async () => {
    try {
      const action = "update";
      const status = isApproved ? "3" : "2";
      
      // Check if credit fields have changed
      const creditFieldsChanged = haveCreditFieldsChanged();
  
      // Create the payload with latest detail_id
      const additionalData = {
        action: action,
        cc_type_limit: "2",
        credit_limit: creditLimit,
        credit_days: creditDays,
        cd_type_limit: "2",
        id: id,
        opening_balance: openingBalance || details.opening_balance || "100.00",
        closing_balance: closingBalance || details.closing_balance || "100.00",
        userid: details.customerid || "1",
        detail_id: detailId || "",
        message: "",
      };
  
      // Filter out discounts that already exist and haven't been modified
      const newDiscounts = discounts.filter(discount => {
        // Keep discounts that are new (isNew flag)
        if (discount.isNew) return true;
  
        // For existing discounts, check if they've been modified
        const originalDiscount = details.products.find(p => p.id === discount.id);
        if (!originalDiscount) return true;
  
        // Check if any field has been modified
        return discount.discount !== originalDiscount.discount ||
          discount.effectiveDate !== originalDiscount.effective_date.split(" ")[0];
      });
  
      // Add only new/modified discounts data with proper indexing
      newDiscounts.forEach((discount, index) => {
        if (discount.product) {
          additionalData[`prod_code[${index}]`] = discount.product;
          additionalData[`discount[${index}]`] = discount.discount;
          additionalData[`discount_amount[${index}]`] = discount.discountedAmount;
          additionalData[`effectivedate[${index}]`] = discount.effectiveDate;
          additionalData[`credit_id[${index}]`] = id;
  
          // For existing discounts, include the ID to update rather than create new
          if (!discount.isNew && discount.id) {
            additionalData[`discount_id[${index}]`] = discount.id;
            additionalData[`did[${index}]`] = discount.id;
          }
        }
      });
  
// Only send NEW vendors that aren't already saved
const existingVendorIds = details.vendors ? details.vendors.map(v => v.vendor_id) : [];
const newVendors = selectedVendor.filter(vendorId => !existingVendorIds.includes(vendorId));

newVendors.forEach((vendorId, vIndex) => {
  additionalData[`vendor_id[${vIndex}]`] = vendorId;
  additionalData[`credit_id[${vIndex}]`] = id;
});
      const result = await handleApiCall(action, status, additionalData);
  
      // ✅ ONLY add to credit history if credit fields changed
      if (creditFieldsChanged) {
        const newHistoryEntry = {
          id: detailId || Date.now().toString(),
          ref_id: id,
          status: status,
          credit_limit: creditLimit,
          credit_days: creditDays,
          cc_type_limit: "2",
          cd_type_limit: "2",
          datetime: new Date().toISOString().replace('T', ' ').substring(0, 19),
          message: "Credit settings updated"
        };
      
  
        setCreditHistory(prev => [...prev, newHistoryEntry]);
        
        // Update the ref to current values after successful save
        previousCreditFields.current = {
          creditLimit,
          creditLimitControl:"2",
          creditDays,
          creditDaysControl:"2",
        };
        console.log("Credit history added due to field changes");
      } else {
        console.log("No credit field changes detected - skipping history entry");
      }
  
      dispatch(fetchCreditDetails(id));
  
      setIsEditingCredit(false);
      setEditingDiscountIndex(null);
  
      console.log("Save Changes - Credit Fields Changed:", haveCreditFieldsChanged());
console.log("Previous fields:", previousCreditFields.current);
console.log("Current fields:", {
  creditLimit,
  creditLimitControl,
  creditDays, 
  creditDaysControl
});

      toast({
        title: "Changes Saved",
        description: creditFieldsChanged 
          ? "Credit settings and products have been updated successfully."
          : "Product changes have been updated successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Failed to save changes:", error);
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save changes",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [creditLimit, creditLimitControl, creditDays, creditDaysControl, discounts, id, handleApiCall, isApproved, detailId, details, toast, openingBalance, closingBalance, haveCreditFieldsChanged, dispatch, selectedVendor]);
  const handleCommentSubmit = useCallback(async () => {
    try {
      const additionalData = {
        id: id,
        userid: details.customerid || "1",
        detail_id: detailId || "",
        message: comment,
        status: "2"   // ✅ Correct: Commented
      };

      await handleApiCall("update_status", "2", additionalData); // ✅ Correct
      onCommentClose();
      setComment("");
    } catch (error) {
      console.error("Comment submission failed:", error);
    }
  }, [comment, handleApiCall, onCommentClose, id, details, detailId]);

  const handleEditCredit = useCallback(() => {
    if (latestCreditEntry) {
      setCreditLimit(latestCreditEntry.credit_limit || "");
      setCreditDays(latestCreditEntry.credit_days || "");
      setCreditLimitControl(latestCreditEntry.cc_type_limit || "2");
      setCreditDaysControl(latestCreditEntry.cd_type_limit || "2");
      
      // ADD THIS: Store initial values when editing starts
      initialCreditData.current = {
        creditLimit: latestCreditEntry.credit_limit || "",
        creditLimitControl: latestCreditEntry.cc_type_limit || "2",
        creditDays: latestCreditEntry.credit_days || "",
        creditDaysControl: latestCreditEntry.cd_type_limit || "2",
        discounts: JSON.parse(JSON.stringify(discounts))
      };
    }
    setIsEditingCredit(true);
  }, [latestCreditEntry, discounts]);

// In parent component's handleCancelEdit:
const handleCancelEdit = useCallback(() => {
  // Reset to initial values
  setCreditLimit(initialCreditData.current.creditLimit);
  setCreditLimitControl(initialCreditData.current.creditLimitControl);
  setCreditDays(initialCreditData.current.creditDays);
  setCreditDaysControl(initialCreditData.current.creditDaysControl);
  setDiscounts(JSON.parse(JSON.stringify(initialCreditData.current.discounts)));
  setIsEditingCredit(false);
  setEditingDiscountIndex(null); // Also reset editing index
}, []); // Add empty dependency array

  // Effects
  useEffect(() => {
    if (id) {
      dispatch(fetchCreditDetails(id));
      dispatch(get_all_vendor_list());
      dispatch(get_full_product_list());
    }
  }, [dispatch, id]);


// ============================================
// FIX 1: Parent Component (CreditDetails) - Update useEffect to deduplicate vendors
// ============================================

useEffect(() => {
  if (details) {
    // Set credit history from details array
    if (details?.details?.[0]?.data && Array.isArray(details.details[0].data)) {
      setCreditHistory(details.details[0].data);
    }

    if (details.transactions && details.transactions.length > 0 &&
      details.transactions[0].reponselist) {
      setTransactionHistory(details.transactions[0].reponselist);
    } else {
      setTransactionHistory([]);
    }

    // ✅ FIX: Properly set selected vendors from API response AND deduplicate
    if (details.vendors && Array.isArray(details.vendors) && details.vendors.length > 0) {
      // Extract vendor_id and remove duplicates using Set
      const vendorIds = [...new Set(details.vendors.map(v => v.vendor_id))];
      setSelectedVendor(vendorIds);
    } else {
      // Reset to empty if no vendors
      setSelectedVendor([]);
    }

    if (details.products?.length > 0) {
      const productDiscounts = details.products.map((product) => {
        const matchedProduct = full_product_list.find(p => p.uuid === product.productid);

        return {
          id: product.id,
          product: product.productid || "",
          type: matchedProduct?.type || "",
          rspCyl: matchedProduct?.rsp || 0,
          rspKg: matchedProduct?.capacity
            ? (parseFloat(matchedProduct.rsp) / parseFloat(matchedProduct.capacity)).toFixed(2)
            : 0,
          discount: product.discount || "",
          taxableAmount: (
            parseFloat(matchedProduct?.rsp || 0) -
            (parseFloat(matchedProduct?.rsp || 0) * parseFloat(product.discount || 0) / 100)
          ).toFixed(2),
          effectiveDate: product.effective_date
            ? product.effective_date.split(" ")[0]
            : new Date().toISOString().split("T")[0],
          isNew: false,
          createdat: product.createdat,
        };
      });

      setDiscounts(productDiscounts);
    }

    // Get the current status from the latest entry
    const latestStatus = currentStatus;

    if (details?.details?.[0]?.data && details.details[0].data.length > 0) {
      const latest = details.details[0].data[details.details[0].data.length - 1];

      // For under review status (0), use requested values and sync closing with opening
      if (latestStatus === "0") {
        const initialOpeningBalance = details.opening_balance || "";
        setOpeningBalance(initialOpeningBalance);
        setClosingBalance(initialOpeningBalance);
        setCreditLimit(details.req_credit_limit || "");
        setCreditDays(details.req_credit_days || "");
        setCreditLimitControl(details.cc_type_limit || "2");
        setCreditDaysControl(details.cd_type_limit || "2");
        
        previousCreditFields.current = {
          creditLimit: details.req_credit_limit || "",
          creditLimitControl: details.cc_type_limit || "2",
          creditDays: details.req_credit_days || "",
          creditDaysControl: details.cd_type_limit || "2"
        };
      } else {
        // For approved/rejected statuses, use the saved values (read-only)
        const finalCreditLimit = latest.credit_limit || details.req_credit_limit || "";
        const finalCreditDays = latest.credit_days || details.req_credit_days || "";
        const finalCreditLimitControl = latest.cc_type_limit || details.cc_type_limit || "2";
        const finalCreditDaysControl = latest.cd_type_limit || details.cd_type_limit || "2";
        
        setCreditLimit(finalCreditLimit);
        setCreditDays(finalCreditDays);
        setCreditLimitControl(finalCreditLimitControl);
        setCreditDaysControl(finalCreditDaysControl);
        setOpeningBalance(details.opening_balance || "");
        setClosingBalance(details.closing_balance || "");
        
        previousCreditFields.current = {
          creditLimit: finalCreditLimit,
          creditLimitControl: finalCreditLimitControl,
          creditDays: finalCreditDays,
          creditDaysControl: finalCreditDaysControl
        };
      }
    } else {
      // Fallback to main details if no history
      const initialOpeningBalance = details.opening_balance || "";
      const fallbackCreditLimit = details.req_credit_limit || "";
      const fallbackCreditDays = details.req_credit_days || "";
      const fallbackCreditLimitControl = details.cc_type_limit === "1" ? "2" : (details.cc_type_limit || "2");
      const fallbackCreditDaysControl = details.cd_type_limit || "2";
      
      setOpeningBalance(initialOpeningBalance);
      if (latestStatus === "0") {
        setClosingBalance(initialOpeningBalance);
      } else {
        setClosingBalance(details.closing_balance || "");
      }
      setCreditLimit(fallbackCreditLimit);
      setCreditDays(fallbackCreditDays);
      setCreditLimitControl(fallbackCreditLimitControl);
      setCreditDaysControl(fallbackCreditDaysControl);
      
      previousCreditFields.current = {
        creditLimit: fallbackCreditLimit,
        creditLimitControl: fallbackCreditLimitControl,
        creditDays: fallbackCreditDays,
        creditDaysControl: fallbackCreditDaysControl
      };
    }

    initialCreditData.current = {
      creditLimit: previousCreditFields.current.creditLimit,
      creditLimitControl: previousCreditFields.current.creditLimitControl,
      creditDays: previousCreditFields.current.creditDays,
      creditDaysControl: previousCreditFields.current.creditDaysControl,
      discounts: JSON.parse(JSON.stringify(details.products?.length > 0 ? details.products.map((product) => {
        const matchedProduct = full_product_list.find(p => p.uuid === product.productid);
        return {
          id: product.id,
          product: product.productid || "",
          type: matchedProduct?.type || "",
          rspCyl: matchedProduct?.rsp || 0,
          rspKg: matchedProduct?.capacity
            ? (parseFloat(matchedProduct.rsp) / parseFloat(matchedProduct.capacity)).toFixed(2)
            : 0,
          discount: product.discount || "",
          taxableAmount: (
            parseFloat(matchedProduct?.rsp || 0) -
            (parseFloat(matchedProduct?.rsp || 0) * parseFloat(product.discount || 0) / 100)
          ).toFixed(2),
          effectiveDate: product.effective_date
            ? product.effective_date.split(" ")[0]
            : new Date().toISOString().split("T")[0],
          isNew: false,
          createdat: product.createdat,
        };
      }) : []))
    };
  }
}, [details, full_product_list, currentStatus]);
  // Loading and error states
  if (detailsLoading || all_loading) {
    return (
      <GasCylinderLoader size="sm" loadingText="Loading Credit details"/>
    );
  }

  if (detailsError) {
    return (
      <Box bg="gray.50" minH="100vh" p={6}>
        <Alert status="error"><AlertIcon />{detailsError}</Alert>
      </Box>
    );
  }

  if (!details) {
    return (
      <Box bg="gray.50" minH="100vh" p={6}>
        <Alert status="info"><AlertIcon />No credit details found.</Alert>
      </Box>
    );
  }

  // Render components
  const renderDocumentViewer = () => (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>View Document</ModalHeader>
        <ModalCloseButton />
        <ModalBody display="flex" justifyContent="center" alignItems="center">
          {selectedDoc ? (
            // Check if it's a PDF or image
            selectedDoc.toLowerCase().endsWith('.pdf') ? (
              <embed
                src={selectedDoc}
                type="application/pdf"
                width="100%"
                height="600px"
              />
            ) : (
              <Image
                src={selectedDoc}
                alt="Document"
                maxH="70vh"
                objectFit="contain"
              />
            )
          ) : (
            <Text>No document available</Text>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );

  const renderCommentModal = () => (
    <Modal isOpen={isCommentOpen} onClose={onCommentClose}>
      <ModalOverlay />
      selectedVe <ModalContent>
        <ModalHeader>Add Comment</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <Text fontSize="sm" color="gray.600">Enter your comment:</Text>
            <Input
              as="textarea"
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Type your comment here..."
            />
            <Button bg={"#347433"} color={"white"} onClick={handleCommentSubmit}>
              Submit
            </Button>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );

  const renderDiscountRow = (row, index, isMobile = false) => {
    const isEditing = editingDiscountIndex === index;
    const isNewProduct = row.isNew;

    // FIX: Only disable editing if rejected, allow editing for approved
    const shouldDisableEditing = isRejected;
    const shouldShowActions = !isRejected;

    if (isMobile) {
      return (
        <Box
          key={index}
          p={4}
          borderTop="1px solid"
          borderColor="gray.200"
          bg="white"
          _hover={{ bg: "gray.50" }}
        >
          <Flex justify="space-between" align="center" mb={3}>
            <Text fontSize="sm" fontWeight="bold" color="blue.600">
              Discount #{index + 1}
            </Text>

            {isNewProduct && (
              <Badge colorScheme="green" ml={2} fontSize="xs">
                New
              </Badge>
            )}

            {shouldShowActions && (
              <Flex>
                {isEditing ? (
                  <IconButton
                    size="xs"
                    colorScheme="green"
                    icon={<Text fontSize="xs">✓</Text>}
                    onClick={() => handleSaveDiscount(index)}
                    mr={2}
                  />
                ) : (
                  <IconButton
                    size="xs"
                    colorScheme="blue"
                    icon={<EditIcon size={14} />}
                    onClick={() => handleEdit(index)}
                    mr={2}
                    isDisabled={shouldDisableEditing}
                  />
                )}
                <IconButton
                  size="xs"
                  colorScheme="red"
                  icon={<DeleteIcon size={14} />}
                  onClick={() => handleDelete(index)}
                  isDisabled={shouldDisableEditing}
                />
              </Flex>
            )}
          </Flex>

          <Stack spacing={3}>
            {/* Type */}
            <Box>
              <Text fontSize="xs" fontWeight="bold" mb={1} color="gray.600">
                Type
              </Text>
              <Select
                value={row.type}
                onChange={(e) => handleChange(index, "type", e.target.value)}
                size="sm"
                placeholder="Select type"
                isDisabled={!isEditing || shouldDisableEditing}
              >
                {[...new Set(full_product_list.map((p) => p.type))].map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </Box>

            {/* Product */}
            <Box>
              <Text fontSize="xs" fontWeight="bold" mb={1} color="gray.600">
                Product
              </Text>
              <Select
                value={row.product}
                onChange={(e) => handleChange(index, "product", e.target.value)}
                size="sm"
                placeholder="Select product"
                isDisabled={!row.type || !isEditing || shouldDisableEditing}
              >
                {getProductsByType(row.type, row.product).map((p) => (
                  <option key={p.uuid} value={p.uuid}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </Box>

            <SimpleGrid columns={2} spacing={3}>
              <Box>
                <Text fontSize="xs" fontWeight="bold" mb={1} color="gray.600">
                  RSP (Per Cyl.)
                </Text>
                <Input value={row.rspCyl} size="sm" type="number" readOnly />
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="bold" mb={1} color="gray.600">
                  RSP (Per Kg)
                </Text>
                <Input value={row.rspKg} size="sm" type="number" readOnly />
              </Box>
            </SimpleGrid>

            {/* Discount */}
            <Box>
              <Text fontSize="xs" fontWeight="bold" mb={1} color="gray.600">
                Discount on RSP (Per Cyl.)
              </Text>
              <Input
                value={row.discount}
                onChange={(e) =>
                  handleChange(index, "discount", e.target.value)
                }
                size="sm"
                type="number"
                isDisabled={!isEditing || shouldDisableEditing}
              />
            </Box>

            {/* Effective Date */}
            <Box>
              <Text fontSize="xs" fontWeight="bold" mb={1} color="gray.600">
                Effective Date
              </Text>
              <Input
                type="date"
                value={row.effectiveDate}
                onChange={(e) =>
                  handleChange(index, "effectiveDate", e.target.value)
                }
                size="sm"
                isDisabled={!isEditing || shouldDisableEditing}
              />
            </Box>
          </Stack>
        </Box>
      );
    }

    // 🖥️ Desktop Row
    return (
      <Flex
        key={index}
        p={3}
        borderTop="1px solid"
        borderColor="gray.200"
        align="center"
        _hover={{ bg: "gray.50" }}
      >
        {/* Type */}
        <Box flex="1" minW="140px" pr={3}>
          <Select
            value={row.type}
            onChange={(e) => handleChange(index, "type", e.target.value)}
            size="sm"
            placeholder="Select type"
            isDisabled={!isEditing || shouldDisableEditing}
          >
            {[...new Set(full_product_list.map((p) => p.type))].map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </Box>

        {/* Product */}
        <Box flex="1" minW="140px" pr={3}>
          <Select
            value={row.product}
            onChange={(e) => handleChange(index, "product", e.target.value)}
            size="sm"
            placeholder="Select product"
            isDisabled={!row.type || !isEditing || shouldDisableEditing}
          >
            {getProductsByType(row.type, index).map((p) => (
              <option key={p.uuid} value={p.uuid}>
                {p.name || p.productname || `Product ${p.uuid}`}
              </option>
            ))}
            {/* Show existing selection if missing */}
            {row.product &&
              !getProductsByType(row.type, index).find(
                (p) => p.uuid === row.product
              ) && (
                <option key={row.product} value={row.product}>
                  {full_product_list.find((p) => p.uuid === row.product)?.name ||
                    full_product_list.find((p) => p.uuid === row.product)
                      ?.productname ||
                    `Product ${row.product}`}
                </option>
              )}
          </Select>
        </Box>

        {/* RSP */}
        <Box flex="1" minW="120px" pr={3}>
          <Input value={row.rspCyl} size="sm" type="number" readOnly />
        </Box>
        <Box flex="1" minW="120px" pr={3}>
          <Input value={row.rspKg} size="sm" type="number" readOnly />
        </Box>

        {/* Discount */}
        <Box position="relative" flex="1" minW="140px" pr={3}>
          <Input
            value={row.discount}
            onChange={(e) => {
  const value = e.target.value;

  if (value > 100) {
    toast({
      title: "Invalid Discount",
      description: "Discount cannot exceed 100%",
      status: "error",
    });
    return;
  }

  handleChange(index, "discount", value);

  // ⭐ ADD THESE LINES
  const rsp = parseFloat(row.rspCyl) || 0;
  const amount = ((rsp * value) / 100).toFixed(2);

  const updated = [...discounts];
  updated[index].discountedAmount = amount;
  setDiscounts(updated);
}}

            size="sm"
            type="number"
            isDisabled={!isEditing || shouldDisableEditing}
            pr="30px"
            max={100}
            min={0}
            onBlur={(e) => {
              if (e.target.value > 100) {
                handleChange(index, "discount", 100);
                toast({
                  title: "Discount Adjusted",
                  description: "Discount has been set to maximum 100%",
                  status: "warning",
                  duration: 3000,
                  isClosable: true,
                });
              }
            }}
          />
          <Text
            position="absolute"
            right="20px"
            top="8px"
            color="gray.500"
            fontSize="sm"
            pointerEvents="none"
          >
            %
          </Text>
        </Box>

        {/* Discounted Amount (Deducted) */}
        <Box flex="1" minW="140px" pr={3}>
          <Input
            value={
              row.discount && row.rspCyl
                ? ((row.rspCyl * row.discount) / 100).toFixed(2)
                : 0
            }
            size="sm"
            type="number"
            readOnly
          />
        </Box>

        {/* Taxable Amount */}
        <Box flex="1" minW="120px" pr={3}>
          <Input
            value={row.taxableAmount || 0}
            size="sm"
            type="number"
            readOnly
          />
        </Box>

        {/* Effective Date */}
        <Box flex="1" minW="140px" pr={3}>
          <Input
            type="date"
            value={row.effectiveDate}
            onChange={(e) =>
              handleChange(index, "effectiveDate", e.target.value)
            }
            size="sm"
            isDisabled={!isEditing || shouldDisableEditing}
          />
        </Box>

        {isNewProduct && (
          <Badge colorScheme="green" ml={2} fontSize="xs">
            New
          </Badge>
        )}

        {/* Actions */}
        <Box w="100px" textAlign="center">
          {shouldShowActions && (
            <>
              {isEditing ? (
                <IconButton
                  size="sm"
                  colorScheme="green"
                  icon={<Text fontSize="sm">✓</Text>}
                  onClick={() => handleSaveDiscount(index)}
                  aria-label="Save discount"
                  mr={1}
                />
              ) : (
                <IconButton
                  size="sm"
                  colorScheme="blue"
                  icon={<RiEditLine size={16} />}
                  onClick={() => handleEdit(index)}
                  aria-label="Edit discount"
                  mr={1}
                  isDisabled={shouldDisableEditing}
                />
              )}
              <IconButton
                size="sm"
                colorScheme="red"
                icon={<RiDeleteBin7Line size={16} />}
                onClick={() => handleDelete(index)}
                aria-label="Delete discount"
                isDisabled={shouldDisableEditing}
              />
            </>
          )}
        </Box>
      </Flex>
    );
  };

  return (
    <>
      <Card
        bg="#fff"
        rounded="md"
        display={"flex"}
        flexDirection={"row"}
        alignItems={"center"}
        justify={"space-between"}
        paddingInline={5}
        paddingBlock={2}
        fontWeight="bold"
        fontSize={{ base: "md", md: "lg" }}
      >
        <Text>Credit Details</Text>
        <CustomButton
          icon={<BiArrowBack color="#fff" />}
          title={"Back"}
          height={"30px"}
          b_color={"#000"}
          onClick={() => navigate(-1)}
        />
      </Card>

      {/* Customer Information Grid */}
      <Grid
        templateColumns={{ base: "1fr", md: "1fr 1fr" }}
        gap={6}
        alignItems="start"
      >
        {/* ===== LEFT COLUMN (50%) ===== */}
        <Stack spacing={6}>
          {/* Customer Information */}
          <Box bg="white" p={{ base: 4, md: 6 }} rounded="md" shadow="sm">
            <Text className="txt" fontWeight="bold" mb={4} fontSize={{ base: "md", md: "lg" }}>
              Customer Information
            </Text>
            <Stack spacing={2} fontSize="sm">
              {[
                { label: "Name:", value: customerName, maxW: "60%" },
                { label: "Type:", value: details.customer_type === "0" ? "Individual" : "Business" },
                { label: "Mobile:", value: details.mobile || "N/A" },
                { label: "Email:", value: details.email || "N/A", maxW: "60%" },
                { label: "Address:", value: details.full_address || "N/A", maxW: "60%" },
              ].map((item, index) => (
                <Flex key={index} justify="space-between" flexWrap="wrap" gap={2}>
                  <Text className="txt">{item.label}</Text>
                  <Text
                    className="txt"
                    fontWeight={item.label === "Name:" ? "medium" : "normal"}
                    maxW={item.maxW}
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    title={item.value}
                  >
                    {item.value}
                  </Text>
                </Flex>
              ))}
            </Stack>
          </Box>

          {/* Business Details */}
          <Box bg="white" p={{ base: 4, md: 6 }} rounded="md" shadow="sm">
            <Text className="txt" fontWeight="bold" mb={4} fontSize={{ base: "md", md: "lg" }}>
              Business Details
            </Text>
            <Stack spacing={2} fontSize="sm">
              {[
                { label: "Est. Monthly Consumption:", value: `₹${details.volume_consumed || "0"}` },
                { label: "Assured Consumption:", value: `₹${details.volume_asured || "0"}` },
                { label: "Requested Credit Limit:", value: `₹${details.req_credit_limit || "0"}`, color: "red.500" },
                { label: "Requested Credit Days:", value: `${details.req_credit_days || "0"} Days` },
              ].map((item, index) => (
                <Flex key={index} justify="space-between" flexWrap="wrap" gap={2}>
                  <Text className="txt">{item.label}</Text>
                  <Text className="txt" color={item.color || "inherit"}>
                    {item.value}
                  </Text>
                </Flex>
              ))}
            </Stack>
          </Box>

          {/* Opening & Closing Balance */}
          <Box bg="white" p={5} borderRadius="lg" boxShadow="sm">
            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
              <FormControl>
                <FormLabel className="txt">Opening Balance</FormLabel>
                <Input
                  value={openingBalance}
                  onChange={(e) => handleOpeningBalanceChange(e.target.value)}
                  placeholder="Enter opening balance"
                  isDisabled={currentStatus === "3" || currentStatus === "1" || isViewOnly}  // ADD isViewOnly
                />
              </FormControl>

              <FormControl>
                <FormLabel className="txt">Closing Balance</FormLabel>
                <Input
                  value={closingBalance}
                  onChange={(e) => setClosingBalance(e.target.value)}
                  placeholder="Enter closing balance"
                  isDisabled
                />
              </FormControl>
            </Grid>
          </Box>

          {/* Vendor Selection */}
          <Box bg="white" p={5} borderRadius="lg" boxShadow="sm">
            <VendorSelection
              all_vendor_list={all_vendor_list}
              selectedVendor={selectedVendor}
              setSelectedVendor={setSelectedVendor}
              isViewOnly={isViewOnly}
              vendorError={vendorError}   // ✅ pass error to show
            />

          </Box>
        </Stack>

        {/* ===== RIGHT COLUMN (50%) ===== */}
        <Box bg="white" p={{ base: 4, md: 6 }} rounded="md" shadow="sm">
          <Text className="txt" fontWeight="bold" mb={4} fontSize={{ base: "md", md: "lg" }}>
            Uploaded Documents
          </Text>
          <Stack spacing={3}>
            {documentTypes.map((doc) => {
              const fileUrl = details[doc.key];
              if (!fileUrl) return null;

              // Check if file is PDF
              const isPdf = fileUrl.toLowerCase().endsWith('.pdf');

              return (
                <Box key={doc.key} p={3} borderWidth="1px" rounded="md" bg="gray.50">
                  <Flex align="center" justify="space-between" mb={2}>
                    <Checkbox className="txt" isChecked size="sm">
                      {doc.label}
                    </Checkbox>
                    <Button
                      size="xs"
                      bg="#347433"
                      color="white"
                      onClick={() => {
                        if (isPdf) {
                          // Directly download PDF
                          const link = document.createElement('a');
                          link.href = fileUrl;
                          link.download = `${doc.label}.pdf`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        } else {
                          // Show modal for images
                          setSelectedDoc(fileUrl);
                          onOpen();
                        }
                      }}
                    >
                      {isPdf ? 'Download' : 'View'}
                    </Button>
                  </Flex>

                  {/* Conditional extra details */}
                  {doc.key === "gst_certificate" && details.gst_number && (
                    <>
                      <Text fontSize="sm" color="gray.700">
                        GST Number: <b>{details.gst_number}</b>
                      </Text>
                      <Text fontSize="sm" color="gray.700">
                        Legal Name: <b>{details.legalname}</b>
                      </Text>
                      <Text fontSize="sm" color="gray.700">
                        Trade Name: <b>{details.tradename}</b>
                      </Text>
                    </>
                  )}
                  {doc.key === "aadhar_card" && details.aadhar_number && (
                    <Text fontSize="sm" color="gray.700">
                      Aadhaar Number: <b>{details.aadhar_number}</b>
                    </Text>
                  )}
                  {doc.key === "pan_card" && details.pan_card_number && (
                    <Text fontSize="sm" color="gray.700">
                      PAN Number: <b>{details.pan_card_number}</b>
                    </Text>
                  )}
                  {doc.key === "business_license" && (
                    <>
                      {details.business_name && (
                        <Text fontSize="sm" color="gray.700">
                          Business Name: <b>{details.business_name}</b>
                        </Text>
                      )}
                      {details.business_type && (
                        <Text fontSize="sm" color="gray.700">
                          Business Type: <b>{details.business_type}</b>
                        </Text>
                      )}
                    </>
                  )}
                  {doc.key === "bank_statement" && (
                    <>
                      {details.account_holder_name && (
                        <Text fontSize="sm" color="gray.700">
                          Account Holder: <b>{details.account_holder_name}</b>
                        </Text>
                      )}
                      {details.bank_account_number && (
                        <Text fontSize="sm" color="gray.700">
                          Account No: <b>{details.bank_account_number}</b>
                        </Text>
                      )}
                      {details.bankname && (
                        <Text fontSize="sm" color="gray.700">
                          Bank: <b>{details.bankname}</b>
                        </Text>
                      )}
                      {details.bank_branch && (
                        <Text fontSize="sm" color="gray.700">
                          Branch: <b>{details.bank_branch}</b>
                        </Text>
                      )}
                    </>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Grid>


      <CreditApprovalSection
        creditLimit={creditLimit}
        setCreditLimit={setCreditLimit}
        creditLimitControl={creditLimitControl}
        setCreditLimitControl={setCreditLimitControl}
        creditDays={creditDays}
        setCreditDays={setCreditDays}
        creditDaysControl={creditDaysControl}
        setCreditDaysControl={setCreditDaysControl}
        discounts={discounts}
        setDiscounts={setDiscounts}
        editingDiscountIndex={editingDiscountIndex}
        setEditingDiscountIndex={setEditingDiscountIndex}
        isEditingCredit={isEditingCredit}
        setIsEditingCredit={setIsEditingCredit}
        isSubmitting={isSubmitting}
        isUnderReview={isUnderReview}
        isApproved={isApproved}
        isRejected={isRejected}
        shouldShowEditButton={shouldShowEditButton}
        latestCreditEntry={latestCreditEntry}
        full_product_list={full_product_list}
        all_vendor_list={all_vendor_list}
        selectedVendor={selectedVendor}
        setSelectedVendor={setSelectedVendor}
        handleAddDiscount={handleAddDiscount}
        handleChange={handleChange}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
        handleSaveDiscount={handleSaveDiscount}
        handleApprove={handleApprove}
        handleReject={handleReject}
        handleSaveChanges={handleSaveChanges}
        handleEditCredit={handleEditCredit}
        handleCancelEdit={handleCancelEdit}
        onCommentOpen={onCommentOpen}
        getProductsByType={getProductsByType}
        toast={toast}
        isViewOnly={isViewOnly}
        setVendorError={setVendorError}
      />

      <Box mt={6}>
        <CreditApprovalHistory
          creditHistory={creditHistory}
          shouldShowCreditHistory={shouldShowCreditHistory}
          transactionHistory={transactionHistory}
        />
      </Box>

      {renderDocumentViewer()}
      {renderCommentModal()}
    </>
  );
};

export default CreditDetails;